import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { 
  School,
  BookOpen,
  CreditCard,
  Building2,
  FileSpreadsheet,
  GraduationCap,
  Calendar,
  Users,
  Database,
  ChevronRight,
  Bell,
  Clock,
  GitBranch,
  DollarSign,
  LayoutGrid,
  Settings
} from 'lucide-react';

const SettingItem = ({ icon: Icon, title, description, path }) => {
  const location = useLocation();
  const isActive = location.pathname === path;

  return (
    <Link 
      to={path}
      className={`block w-full group ${
        isActive ? 'bg-[#202654]/5' : 'hover:bg-[#202654]/5'
      }`}
    >
      <div className="flex items-center px-6 py-5 cursor-pointer transition-all duration-200">
        <div className="flex-shrink-0">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-200 ${
            isActive 
              ? 'bg-[#202654] text-white'
              : 'bg-[#202654]/90 text-white'
          }`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
        <div className="ml-5 flex-1">
          <h3 className={`text-base font-semibold ${
            isActive ? 'text-[#202654]' : 'text-gray-900 group-hover:text-[#202654]'
          }`}>
            {title}
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            {description}
          </p>
        </div>
        <div className="ml-4 flex-shrink-0 group-hover:translate-x-2 transition-transform duration-200">
          <ChevronRight className={`h-6 w-6 ${isActive ? 'text-[#202654]' : 'text-[#202654]/70'}`} />
        </div>
      </div>
    </Link>
  );
};

const SettingsSection = ({ title, items }) => (
  <div className="bg-white rounded-2xl shadow-md mb-8 overflow-hidden border border-gray-100">
    <div className="px-6 py-5 bg-[#202654]">
      <h2 className="text-lg font-semibold text-white/90">{title}</h2>
    </div>
    <div>
      {items.map((item, index) => (
        <div key={index} className="border-t first:border-t-0 border-gray-100">
          <SettingItem {...item} />
        </div>
      ))}
    </div>
  </div>
);

const SettingsPage = () => {
  const navigate = useNavigate();
  const settingsSections = [
    {
      title: "Configuration Établissement",
      items: [
        {
          icon: Building2,
          title: "Informations établissement",
          description: "Modifiez les informations de l'école",
          path: "/etablissement"
        },
        {
          icon: LayoutGrid,
          title: "Salles de classe",
          description: "Gérez les salles et leur capacité",
          path: "/salle"
        },
        {
          icon: Users,
          title: "Personnel",
          description: "Gérez les utilisateurs et leurs rôles",
          path: "/personnels"
        },
        {
          icon: School,
          title: "Départements",
          description: "Organisez les départements et filières",
          path: "/departements"
        }
      ]
    },
    {
      title: "Configuration Académique",
      items: [

        {
          icon: Calendar,
          title: "Années académiques",
          description: "Définissez les années scolaires et semestres",
          path: "/annee-academique"
        },
        {
          icon: Clock,
          title: "Trimestres",
          description: "Gérez les trimestres et leurs périodes",
          path: "/liste-semestres"
        },
        {
          icon: GitBranch,
          title: "Filières",
          description: "Configurez les filières et leurs spécialités",
          path: "/liste-filieres"
        },
        {
          icon: GraduationCap,
          title: "Niveaux d'études",
          description: "Configurez les niveaux et parcours",
          path: "/liste-niveaux"
        },
        {
          icon: FileSpreadsheet,
          title: "Modules",
          description: "Gérez les modules et leurs affectations",
          path: "/modules"
        },
       
        {
          icon: LayoutGrid,
          title: "Classes",
          description: "Gérez les classes et leurs compositions",
          path: "/classes"
        },
       
        {
          icon: BookOpen,
          title: "Unités d'enseignement",
          description: "Gérez les UE, leurs coefficients et crédits",
          path: "/unite-enseignement"
        }
      ]
    },
    {
      title: "Configuration Financière",
      items: [
        {
          icon: FileSpreadsheet,
          title: "Prix des vacations",
          description: "Définissez les taux horaires des enseignants",
          path: "/tarification"
        },
        {
          icon: DollarSign,
          title: "Frais d'inscription",
          description: "Définissez les frais d'inscription des étudiants",
          path: "/frais-inscription"
        },
        {
          icon: CreditCard,
          title: "Tarifs étudiants",
          description: "Configurez les frais de scolarité",
          path: "/tarification"
        },
        {
          icon: CreditCard,
          title: "Modalités de paiement",
          description: "Gérez les échéances et modes de paiement",
          path: "/modalites-paiement"
        }
      ]
    },
   
    {
      title: "Configuration Système",
      items: [
        {
          icon: Database,
          title: "Base de données",
          description: "Sauvegardez et restaurez vos données",
          path: "/settings/system/database"
        },
        {
          icon: Bell,
          title: "Notifications",
          description: "Paramétrez les notifications système",
          path: "/settings/system/notifications"
        }
      ]
    }
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Card principale avec design professionnel */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Header avec bannière bleue */}
            <div className="bg-gradient-to-r from-[#202654] to-[#202654]/90 px-6 py-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center text-white">
                  <button
                    onClick={() => navigate(-1)}
                    className="mr-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  <div className="bg-white/10 p-3 rounded-xl mr-4">
                    <Settings className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold">Paramètres</h1>
                    <p className="mt-1 text-blue-100">
            Gérez les paramètres de votre établissement
          </p>
                  </div>
                </div>
              </div>
        </div>

            {/* Contenu principal */}
            <div className="p-6">
              <div className="max-w-5xl mx-auto">
        <div className="space-y-8">

          {settingsSections.map((section, index) => (
            <SettingsSection
              key={index}
              title={section.title}
              items={section.items}
            />
          ))}
        </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SettingsPage;